export interface QueueActionBase {
  type: 'complete'|'on_deck'|'queued'|'advance'|'queue_first'|'select_winner_and_advance';
  label: string;
  description?: string;
  earliestQueueTime?: number;
  doubleQueueConfirmation?: string;
  lastActionTime: number;
  expectedTime?: number;
};

export interface SingleQueueAction extends QueueActionBase {
  type: 'complete'|'on_deck'|'queued';
  match: string;
}

export interface AdvanceQueueAction extends QueueActionBase {
  type: 'advance';
  matchToOnField: string;
  matchToOnDeck?: string;
  matchToQueue?: string;
}

export interface QueueFirstAction extends QueueActionBase {
  type: 'queue_first';
  matchToStraightToOnDeck: string;
  matchToQueue?: string;
  matchToAlsoQueue?: string;
}

export interface PlayoffQueueAction extends QueueActionBase {
  type: 'select_winner_and_advance';
  matchToSelectWinner: string;
  matchToOnField?: string;
  matchToOnDeck?: string;
  matchToQueue?: string;
}


export type QueueAction = SingleQueueAction|AdvanceQueueAction|QueueFirstAction|PlayoffQueueAction;

export type MatchStatus = 'waiting'|'queued'|'on_deck'|'complete';

export interface MatchWithKey extends Match {
  key: string;
  label: string;
  description?: string;
  redUndecidedLabelShort?: string;
  blueUndecidedLabelShort?: string;
  current?: boolean;
  estimatedQueueTime?: number;
  estimatedOnDeckTime?: number;
  estimatedOnFieldTime?: number;
  estimatedStartTime?: number;
  queueTimeLocal?: string;

  // How many milliseconds ago was this match set as on field.
  onFieldMsAgo?: number;
}

export interface Match {
  blue?: MatchTeam[];
  red?: MatchTeam[];
  redAlliance?: number;
  blueAlliance?: number;
  redUndecidedLabel?: string;
  blueUndecidedLabel?: string;
  redLineupSet?: boolean;
  blueLineupSet?: boolean;
  status: MatchStatus;
  uncertain?: boolean;
  queueBeforeOpeningCeremonies?: boolean;
  filler?: boolean;
  time?: number;
  queueTime?: number;
  breakAfter?: string;
  breakDurationMinutes?: number;
  breakOverrun?: boolean;
  pseudoBreakAfter?: boolean;
  timestamps?: {complete?: number, queued?: number, on_deck?: number, started?: number, committed?: number};
  winner?: 'red'|'blue';
  round?: number;
  advancement?: MatchAdvancement,
  finalsType?: 'best2of3'|'red1blue2';
  placement?: string;
  location?: string;
  locationFieldNumber?: number;
  showBracket?: boolean;
  afterMatchId?: string;
  replayOfMatchId?: string;

  timeInterval?: number; // For einstein matches, the time interval between the previous match and this one
}

export interface MatchAdvancement {
  winner: {
    match: string;
    color: 'red'|'blue';
    description?: string;
  },
  loser?: {
    match: string;
    color: 'red'|'blue';
    description?: string;
  },
}

export type MatchTeamStatus = 'waiting'|'human'|'checked_in'|'early';

export interface MatchTeam {
  team: string;
  scheduledTeam?: string;
  status: MatchTeamStatus;
  checkedInAt?: number;
}

export interface Task {
  created: number;
  completed: number;
  id: string;
  match?: string;
}

export interface TaskDefinition {
  id: string;
  title: string;
  description?: string;
  when?: string;
  open?: boolean;
  type?: 'info'|'secondary';
  actions?: TaskAction[];
  input?: 'queueTime'|'break';
  noActionRequired?: boolean;
  dynamicDescription?: 'teamNotifications'|'winner';
}

export interface TaskAction {
  text: string;
  dismiss: boolean;
  alsoDismiss?: string;
  createTask?: string|string[]|{[matchType:string]: string|string[]};
  style?: 'primary'|'secondary'|'hidden'|'red'|'blue';
  call?: string;
  data?: {};
  url?: string;
  clientAction?: 'add-filler-match'|'generate-playoff-matches'|'go-to-next-event';
  hideAfterCompleted?: boolean;
}

export interface FullTask extends Task, TaskDefinition {
  key: string;
}

export interface TeamNotifications {
  [team: string]: TeamNotificationKey;
}

export interface TeamNotificationKey {
  [key: string]: TeamNotification;
}

export interface TeamNotification {
  key: string;
  phoneNumbers?: {[key: string]: string};
  slack?: {
    channel: string;
    channel_id: string;
    configuration_url: string;
    url: string;
    saved?: boolean;
    locale: string;
  },
  discord?: {
    id: string;
    channelId: string;
    token: string;
    url: string;
    saved?: boolean;
    locale: string;
  },
  announcements?: {
    slack?: {
      channel: string;
      channel_id: string;
      configuration_url: string;
      url: string;
      saved?: boolean;
      locale: string;
    },
    discord?: {
      id: string;
      channelId: string;
      token: string;
      url: string;
      saved?: boolean;
      locale: string;
    },
  }
}

export interface TeamNotificationSummary {
  team: string;
  key: string;
  phoneNumberCount: number;
  slack?: boolean;
  discord?: boolean;
  announcementsSlack?: boolean;
  announcementsDiscord?: boolean;
}

export interface FullSetting {
  name: string;
  type: 'number'|'datetime-local'|'text'|'boolean'|'json';
  editable: boolean;
  defaultValue?: string|number|boolean;
  value?: string|number|boolean;
  options?: {[value: string]: string};
}

export interface Rankings {
  asOf: string;
  rankings: TeamRanking[];
}

export interface TeamRanking {
  rank: number;
  team: string;
  record: string;
  played: number;
  dq: number;
  rankingScore?: number;
}

export interface Announcement {
  key: string;
  timestamp?: number;
  title: string;
  localizedTitle: string;
  message: string;
  teams?: string[];
}

export interface MultiAnnouncement extends Announcement {
  eventKeys: string[];
}

export interface PartsRequest {
  key: string;
  timestamp: number;
  team: string;
  parts: string;
  status: 'pending'|'active'|'denied';
}

export interface AllTeamData {
  [team: string]: TeamData;
}

export interface TeamData {
  allianceSelectionCheckIn: boolean;
  pit?: string;
  inspected?: boolean;
  hasPlayed?: string;
  pickAPitWindowOpens?: number;
  custom?: {[key: string]: boolean|string};
}

export interface FtaNotes {
  [team: string]: TeamNotes;
}

export interface FtaNoteRequest {
  requestCsa?: boolean;
  requestLri?: boolean;
}

export interface FtaNoteRequests {
  [team: string]: TeamNoteRequests;
}

export interface TeamNotes {
  [match: string]: string;
}

export interface TeamNoteRequests {
  [match: string]: FtaNoteRequest;
}

export interface EventSummaries {
  [eventKey: string]: EventSummary;
}

export interface EventSummary {
  key: string;
  name: string;
  start: number;
  end: number;
  exactDates?: boolean;
}

export interface Lineups {
  [matchKey: string]: AllianceLineups;
}

export interface AllianceLineups {
  red: string[];
  blue: string[];
}

export interface InspectionSummary {
  status: 'hold'|'in-progress'|'complete'|'reinspection'|'queued'|'not-started';
  inspected?: boolean;
  queueLength?: number;
  queueOrder?: number;
}
